import { useState } from 'react'
import { useTranslation } from '@/i18n'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { IntuiDateTimePicker } from '@/components/intueri/IntuiDatePicker/IntuiDatePicker'
import { apiService, type Expert } from '@/service/api'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Fires after a successful create; parent should refresh the list. */
  onCreated: () => void
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// "Jane Doe <jane@example.com>" or just "jane@example.com"; one per line.
const parseExperts = (raw: string): Expert[] => {
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line): Expert | null => {
      const angle = line.match(/^(.*?)\s*<([^>]+)>\s*$/)
      if (angle) {
        const name = angle[1].trim()
        const email = angle[2].trim()
        if (!EMAIL_RE.test(email)) return null
        return { name, email }
      }
      if (EMAIL_RE.test(line)) return { name: '', email: line }
      return null
    })
    .filter((e): e is Expert => e !== null)
}

// Combine an IntuiDatePicker date + IntuiTimePicker time string ("HH:mm:ss"
// or "HH:mm") into a local-time JS Date so submission converts to UTC via
// .toISOString(). Returns null if either part is missing or invalid.
const combineDateTime = (date: Date | undefined, time: string): Date | null => {
  if (!date || !time) return null
  const [hStr = '0', mStr = '0', sStr = '0'] = time.split(':')
  const h = Number(hStr)
  const m = Number(mStr)
  const s = Number(sStr)
  if (Number.isNaN(h) || Number.isNaN(m) || Number.isNaN(s)) return null
  const out = new Date(date)
  out.setHours(h, m, s, 0)
  return out
}

const CreateTopicDialog = ({ open, onOpenChange, onCreated }: Props) => {
  const { t } = useTranslation()
  const [topic, setTopic] = useState('')
  const [questionsText, setQuestionsText] = useState('')
  const [expertsText, setExpertsText] = useState('')
  const [startDate, setStartDate] = useState<Date | undefined>()
  const [startTime, setStartTime] = useState('09:00:00')
  const [endDate, setEndDate] = useState<Date | undefined>()
  const [endTime, setEndTime] = useState('10:00:00')
  const [submitting, setSubmitting] = useState(false)

  const reset = () => {
    setTopic('')
    setQuestionsText('')
    setExpertsText('')
    setStartDate(undefined)
    setStartTime('09:00:00')
    setEndDate(undefined)
    setEndTime('10:00:00')
    setSubmitting(false)
  }

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next)
    if (!next) reset()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmedTopic = topic.trim()
    const questions = questionsText
      .split(/\r?\n/)
      .map((q) => q.trim())
      .filter(Boolean)
    const experts = parseExperts(expertsText)

    if (!trimmedTopic) {
      toast.error(t('hek:create.errors.topic_required', { defaultValue: 'Topic is required.' }))
      return
    }
    if (questions.length === 0) {
      toast.error(t('hek:create.errors.questions_required', { defaultValue: 'Add at least one question.' }))
      return
    }
    if (experts.length === 0) {
      toast.error(t('hek:create.errors.experts_required', { defaultValue: 'Add at least one expert (Name <email> per line).' }))
      return
    }
    const start = combineDateTime(startDate, startTime)
    const end = combineDateTime(endDate, endTime)
    if (!start || !end) {
      toast.error(t('hek:create.errors.dates_required', { defaultValue: 'Start and end dates are required.' }))
      return
    }
    if (start >= end) {
      toast.error(t('hek:create.errors.dates_order', { defaultValue: 'End must be after start.' }))
      return
    }
    const startISO = start.toISOString()
    const endISO = end.toISOString()

    setSubmitting(true)
    try {
      await apiService.createInterview({
        topic: trimmedTopic,
        questions,
        experts,
        interview_start_date: startISO,
        interview_end_date: endISO,
      })
      toast.success(t('hek:create.success', { defaultValue: 'Topic created.' }))
      handleOpenChange(false)
      onCreated()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('hek:create.errors.failed', { defaultValue: 'Failed to create topic.' }))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[640px]">
        <DialogHeader>
          <DialogTitle>{t('hek:create.title', { defaultValue: 'New Topic' })}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto px-1">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hek-topic">
              {t('hek:create.topic', { defaultValue: 'Topic' })}
            </Label>
            <Input
              id="hek-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={t('hek:create.topic_placeholder', { defaultValue: 'e.g. Pump Failure RCA' })}
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>{t('hek:create.start', { defaultValue: 'Start' })}</Label>
              <IntuiDateTimePicker
                date={startDate}
                time={startTime}
                onDateChange={setStartDate}
                onTimeChange={setStartTime}
                showSeconds={false}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t('hek:create.end', { defaultValue: 'End' })}</Label>
              <IntuiDateTimePicker
                date={endDate}
                time={endTime}
                onDateChange={setEndDate}
                onTimeChange={setEndTime}
                showSeconds={false}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hek-questions">
              {t('hek:create.questions', { defaultValue: 'Questions' })}
            </Label>
            <Textarea
              id="hek-questions"
              value={questionsText}
              onChange={(e) => setQuestionsText(e.target.value)}
              rows={4}
              placeholder={t('hek:create.questions_placeholder', {
                defaultValue: 'One question per line',
              })}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hek-experts">
              {t('hek:create.experts', { defaultValue: 'Experts' })}
            </Label>
            <Textarea
              id="hek-experts"
              value={expertsText}
              onChange={(e) => setExpertsText(e.target.value)}
              rows={3}
              placeholder={t('hek:create.experts_placeholder', {
                defaultValue: 'One per line: Jane Doe <jane@example.com>',
              })}
            />
          </div>
        </form>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
            {t('common:cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={submitting}>
            {submitting
              ? t('hek:create.submitting', { defaultValue: 'Creating…' })
              : t('hek:create.submit', { defaultValue: 'Create Topic' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CreateTopicDialog
